import { useEffect, useMemo } from 'react';
import Paho, { type ConnectionOptions, type Message } from 'paho-mqtt';

interface Params {
  host: string;
  port: number;
  reconnectTimeout?: number;
  userID?: string;
  onConnect?: () => void;
  onFailure?: () => void;
  onConnectionLost?: () => void;
  onMessageArrived: (message: Message) => void;
}

export default function useMQTT({
  host,
  port,
  userID,
  onConnect: _onConnect,
  onFailure: _onFailure,
  onConnectionLost: _onConnectionLost,
  onMessageArrived: _onMessageArrived,
  reconnectTimeout = 2000,
}: Params) {
  const connectOptions = useMemo(
    () =>
      ({
        timeout: 10,
        useSSL: true,
        keepAliveInterval: 360,
        cleanSession: false,
        userName: 'rabbitmq',
        password: 'atpq238rz',
        onSuccess: onConnect,
        onFailure: onFailure,
      } as ConnectionOptions),
    [onConnect, onFailure]
  );

  const mqttClient = useMemo(() => {
    if (!userID) return undefined;

    const client = new Paho.Client(
      host,
      port,
      '/rabbitmq/ws',
      `myclientid_${userID}`
    );

    return client;
  }, [host, port, userID]);

  useEffect(() => {
    if (!mqttClient) return;

    // eslint-disable-next-line no-console
    console.info(`connecting to ${host} ${port} ${userID}`);

    mqttClient.onConnectionLost = onConnectionLost;
    mqttClient.onMessageArrived = _onMessageArrived;

    mqttClient.connect(connectOptions);

    return () => {
      if (mqttClient.isConnected()) mqttClient.disconnect();
    };
  }, [mqttClient]);

  function onFailure() {
    // eslint-disable-next-line no-console
    console.info(`Connection Attempt to Host ${host} Failed`);
    _onFailure?.();
  }

  function onConnect() {
    // eslint-disable-next-line no-console
    console.info('Connected');
    mqttClient?.subscribe(`${userID}/#`);
    _onConnect?.();
    // With a QoS level to 1 set on the message the client will receive acknowledgments from Solace messaging when it has successfully stored the message.
  }

  function onConnectionLost() {
    // eslint-disable-next-line no-console
    console.info('connection lost');

    _onConnectionLost?.();
  }

  return {};
}
